import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Upload, Image as ImageIcon, Loader2, Check, FileText, Save, Calendar, ExternalLink, Link2, Webhook, Palette, Calculator, Globe, Phone, Mail } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { APIIntegrationPage } from './APIIntegrationPage';
import PricingSettingsSection from '../../components/admin/PricingSettingsSection';
import SeoSettings from '../../components/admin/SeoSettings';
import CalendlyMultiUser from '../../components/admin/CalendlyMultiUser';
import SchedulingButtonSettings from '../../components/admin/SchedulingButtonSettings';
import WeeklyReportSettings from '../../components/admin/WeeklyReportSettings';
import LoomyCRMSettings from '../../components/admin/LoomyCRMSettings';

const AdminSettingsPage = () => {
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const [previews, setPreviews] = useState({});
  const [platformName, setPlatformName] = useState('Talent Partner');
  const [savingName, setSavingName] = useState(false);
  
  // Legal pages state
  const [cgvContent, setCgvContent] = useState({ title: '', content: '' });
  const [mentionsContent, setMentionsContent] = useState({ title: '', content: '' });
  const [savingCgv, setSavingCgv] = useState(false);
  const [savingMentions, setSavingMentions] = useState(false);
  const [loadingLegal, setLoadingLegal] = useState(true);
  
  // Calendly integration state
  const [calendlyUrl, setCalendlyUrl] = useState('');
  const [calendlyEnabled, setCalendlyEnabled] = useState(false);
  const [savingCalendly, setSavingCalendly] = useState(false);
  const [loadingCalendly, setLoadingCalendly] = useState(true);

  useEffect(() => {
    fetchBranding();
    fetchLegalPages();
    fetchCalendlySettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCalendlySettings = async () => {
    setLoadingCalendly(true);
    try {
      const response = await api.get('/admin/integrations');
      if (response.ok) {
        const data = await response.json();
        setCalendlyUrl(data.calendly_url || '');
        setCalendlyEnabled(data.calendly_enabled || false);
      }
    } catch (error) {
      console.error('Failed to fetch Calendly settings:', error);
    } finally {
      setLoadingCalendly(false);
    }
  };

  const handleSaveCalendly = async () => {
    setSavingCalendly(true);
    try {
      const response = await api.patch('/admin/integrations/calendly', {
        calendly_url: calendlyUrl || null,
        calendly_enabled: calendlyEnabled
      });
      
      if (response.ok) {
        toast.success('Configuration Calendly sauvegardée');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Failed to save Calendly settings:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingCalendly(false);
    }
  };

  const fetchLegalPages = async () => {
    setLoadingLegal(true);
    try {
      const [cgvRes, mentionsRes] = await Promise.all([
        api.get('/admin/legal/cgv'),
        api.get('/admin/legal/mentions_legales')
      ]);
      
      if (cgvRes.ok) {
        const data = await cgvRes.json();
        setCgvContent({ title: data.title, content: data.content });
      }
      
      if (mentionsRes.ok) {
        const data = await mentionsRes.json();
        setMentionsContent({ title: data.title, content: data.content });
      }
    } catch (error) {
      console.error('Failed to fetch legal pages:', error);
    } finally {
      setLoadingLegal(false);
    }
  };

  const saveLegalPage = async (pageType) => {
    const isCgv = pageType === 'cgv';
    const content = isCgv ? cgvContent : mentionsContent;
    const setSaving = isCgv ? setSavingCgv : setSavingMentions;
    
    setSaving(true);
    try {
      const response = await api.put(`/admin/legal/${pageType}`, content);
      
      if (response.ok) {
        toast.success('Page mise à jour avec succès');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Failed to save legal page:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const fetchBranding = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/branding');
      const data = await response.json();
      setBranding(data);
      
      // Set platform name
      setPlatformName(data.platform_name || 'Talent Partner');
      
      // Set initial previews from existing URLs
      setPreviews({
        logo_light: data.logo_light_url,
        logo_dark: data.logo_dark_url,
        logo_icon: data.logo_icon_url,
        favicon: data.favicon_url
      });
    } catch (error) {
      console.error('Failed to fetch branding:', error);
      toast.error('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (fileType, file) => {
    if (!file) return;
    
    // Validate file type
    const validExtensions = ['.png', '.svg'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!validExtensions.includes(fileExtension)) {
      toast.error('Only PNG and SVG files are allowed');
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviews(prev => ({ ...prev, [fileType]: reader.result }));
    };
    reader.readAsDataURL(file);
    
    // Upload file
    handleUpload(fileType, file);
  };

  const handleUpload = async (fileType, file) => {
    setUploading(prev => ({ ...prev, [fileType]: true }));
    
    try {
      const formData = new FormData();
      formData.append('file_type', fileType);
      formData.append('file', file);
      
      const response = await api.post('/admin/branding/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        
        // Update branding state
        await fetchBranding();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(prev => ({ ...prev, [fileType]: false }));
    }
  };

  const handleSavePlatformName = async () => {
    setSavingName(true);
    try {
      const response = await api.patch('/admin/branding/platform-name', {
        platform_name: platformName
      });
      
      if (response.ok) {
        toast.success('Platform name updated successfully');
        await fetchBranding();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update platform name');
      }
    } catch (error) {
      console.error('Failed to update platform name:', error);
      toast.error('Failed to update platform name');
    } finally {
      setSavingName(false);
    }
  };

  const getBrandingField = (type, label, description, usedOn) => {
    const isUploading = uploading[type];
    const hasFile = previews[type];
    
    return (
      <div className="space-y-3">
        <div>
          <Label className="text-base font-semibold">{label}</Label>
          <p className="text-sm text-muted-foreground mt-1">
            {description}
          </p>
          <div className="text-xs text-muted-foreground mt-2">
            <span className="font-medium">Used on:</span> {usedOn}
          </div>
        </div>

        {/* Preview */}
        {hasFile && (
          <div className="border border-border rounded-lg p-4 bg-muted/30 flex items-center justify-center min-h-[120px]">
            {type === 'logo_dark' ? (
              <div className="bg-[#1E3A5F] p-4 rounded">
                <img 
                  src={previews[type]} 
                  alt={label} 
                  className="max-h-16 max-w-[200px] object-contain"
                />
              </div>
            ) : type === 'favicon' || type === 'logo_icon' ? (
              <img 
                src={previews[type]} 
                alt={label} 
                className="h-16 w-16 object-contain"
              />
            ) : (
              <img 
                src={previews[type]} 
                alt={label} 
                className="max-h-16 max-w-[200px] object-contain"
              />
            )}
          </div>
        )}

        {/* Upload Button */}
        <div>
          <Input
            id={`upload-${type}`}
            type="file"
            accept=".png,.svg"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(type, file);
            }}
            disabled={isUploading}
          />
          <Label htmlFor={`upload-${type}`}>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={isUploading}
              asChild
            >
              <span className="cursor-pointer flex items-center justify-center">
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : hasFile ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Change {label}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload {label}
                  </>
                )}
              </span>
            </Button>
          </Label>
          <p className="text-xs text-muted-foreground mt-2">
            PNG or SVG format • Max 5MB
          </p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8" data-testid="admin-settings-page">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-64" />
            <div className="h-4 bg-muted rounded w-96" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="admin-settings-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-display font-semibold mb-2">Paramètres</h1>
          <p className="text-muted-foreground">Gérez la configuration de votre plateforme</p>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="branding" className="w-full">
          <TabsList className="flex flex-wrap gap-1 h-auto p-1 mb-6">
            <TabsTrigger value="branding" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-branding">
              <Palette className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Branding</span>
              <span className="sm:hidden">Brand</span>
            </TabsTrigger>
            <TabsTrigger value="seo" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-seo">
              <Globe className="h-3 w-3 sm:h-4 sm:w-4" />
              SEO
            </TabsTrigger>
            <TabsTrigger value="pricing" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-pricing">
              <Calculator className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Pricing</span>
              <span className="sm:hidden">Prix</span>
            </TabsTrigger>
            <TabsTrigger value="api" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-api">
              <Webhook className="h-3 w-3 sm:h-4 sm:w-4" />
              API
            </TabsTrigger>
            <TabsTrigger value="legal" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-legal">
              <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Légal</span>
              <span className="sm:hidden">Lég.</span>
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-1 text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-reports">
              <Mail className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Rapports</span>
              <span className="sm:hidden">Rapp.</span>
            </TabsTrigger>
          </TabsList>

          {/* Branding Tab */}
          <TabsContent value="branding">
            <Card className="p-6" data-testid="branding-settings-card">
              <div className="flex items-center gap-3 mb-6">
                <ImageIcon className="h-6 w-6 text-tp-primary" />
                <div>
                  <h2 className="text-2xl font-display font-semibold">Branding</h2>
                  <p className="text-sm text-muted-foreground">
                    Gérez l'identité visuelle de votre plateforme
                  </p>
                </div>
              </div>

              {/* Platform Name */}
              <div className="mb-8 p-6 bg-muted/30 rounded-lg border border-border">
                <Label htmlFor="platform-name" className="text-base font-semibold">
                  Nom de la plateforme
                </Label>
                <p className="text-sm text-muted-foreground mt-1 mb-4">
                  Utilisé dans les titres, la page de connexion, le footer
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    id="platform-name"
                    type="text"
                    value={platformName}
                    onChange={(e) => setPlatformName(e.target.value)}
                    placeholder="ex: Talent Partner"
                    className="w-full sm:max-w-md"
                    data-testid="platform-name-input"
                  />
                  <Button
                    onClick={handleSavePlatformName}
                    disabled={savingName}
                    className="w-full sm:w-auto flex-shrink-0"
                    data-testid="save-platform-name-button"
                  >
                    {savingName ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sauvegarde...
                      </>
                    ) : (
                      'Sauvegarder'
                    )}
                  </Button>
                </div>
              </div>

              <Separator className="my-6" />

              <h3 className="text-lg font-semibold mb-4">Logos</h3>

              <div className="space-y-8">
                {getBrandingField(
                  'logo_light',
                  'Logo (Version claire)',
                  'Logo pour fonds clairs',
                  'En-tête du site, pages publiques'
                )}
                <Separator />
                {getBrandingField(
                  'logo_dark',
                  'Logo (Version sombre)',
                  'Logo pour fonds sombres',
                  'Sidebar admin, emails'
                )}
                <Separator />
                {getBrandingField(
                  'logo_icon',
                  'Icône Logo',
                  'Logo compact pour petits espaces',
                  'Icône sidebar, UI compact'
                )}
                <Separator />
                {getBrandingField(
                  'favicon',
                  'Favicon',
                  'Icône pour les onglets navigateur',
                  'Onglet navigateur, favoris'
                )}
              </div>

              <div className="mt-8 p-4 bg-muted/50 rounded-lg border border-border">
                <p className="text-sm text-muted-foreground">
                  <strong>Note :</strong> Les modifications sont appliquées immédiatement. Rafraîchissez votre navigateur pour voir les changements.
                </p>
              </div>
            </Card>
          </TabsContent>

          {/* SEO Tab */}
          <TabsContent value="seo">
            <Card className="p-6" data-testid="seo-settings-card">
              <div className="flex items-center gap-3 mb-6">
                <Globe className="h-6 w-6 text-tp-accent" />
                <div>
                  <h2 className="text-2xl font-display font-semibold text-tp-text">SEO & Partage Social</h2>
                  <p className="text-sm text-tp-text-light">
                    Configurez les meta tags et l'image de partage pour optimiser votre référencement
                  </p>
                </div>
              </div>
              <SeoSettings />
            </Card>
          </TabsContent>

          {/* API Integration Tab */}
          <TabsContent value="api">
            <APIIntegrationPage />
            <div className="mt-6">
              <LoomyCRMSettings />
            </div>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing">
            <Card className="p-6" data-testid="pricing-settings-card">
              <div className="flex items-center gap-3 mb-6">
                <Calculator className="h-6 w-6 text-tp-accent" />
                <div>
                  <h2 className="text-2xl font-display font-semibold text-tp-text">Pricing & Rentabilité</h2>
                  <p className="text-sm text-tp-text-light">
                    Configurez les paramètres de calcul de coût et de marge
                  </p>
                </div>
              </div>
              <PricingSettingsSection />
            </Card>
          </TabsContent>

          {/* Legal Pages Tab */}
          <TabsContent value="legal">
            <Card className="p-6" data-testid="legal-settings-card">
              <div className="flex items-center gap-3 mb-6">
                <FileText className="h-6 w-6 text-tp-primary" />
                <div>
                  <h2 className="text-2xl font-display font-semibold">Pages légales</h2>
                  <p className="text-sm text-muted-foreground">
                    Modifiez les CGV et les mentions légales
                  </p>
                </div>
              </div>

              {loadingLegal ? (
                <div className="py-8 text-center text-muted-foreground">
                  Chargement...
                </div>
              ) : (
                <Tabs defaultValue="cgv" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="cgv" data-testid="tab-cgv">
                      CGV
                    </TabsTrigger>
                    <TabsTrigger value="mentions" data-testid="tab-mentions">
                      Mentions Légales
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="cgv" className="space-y-4">
                    <div>
                      <Label htmlFor="cgv-title">Titre de la page</Label>
                      <Input
                        id="cgv-title"
                        value={cgvContent.title}
                        onChange={(e) => setCgvContent({ ...cgvContent, title: e.target.value })}
                        placeholder="Conditions Générales de Vente"
                        className="mt-1.5"
                        data-testid="cgv-title-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="cgv-content">Contenu (Markdown supporté)</Label>
                      <Textarea
                        id="cgv-content"
                        value={cgvContent.content}
                        onChange={(e) => setCgvContent({ ...cgvContent, content: e.target.value })}
                        placeholder="## Article 1..."
                        rows={15}
                        className="mt-1.5 font-mono text-sm"
                        data-testid="cgv-content-input"
                      />
                    </div>
                    <div className="flex justify-between items-center pt-4">
                      <a 
                        href="/cgv" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-tp-primary hover:underline"
                      >
                        Voir la page →
                      </a>
                      <Button 
                        onClick={() => saveLegalPage('cgv')}
                        disabled={savingCgv}
                        data-testid="save-cgv-button"
                      >
                        {savingCgv ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Sauvegarde...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Sauvegarder
                          </>
                        )}
                      </Button>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="mentions" className="space-y-4">
                    <div>
                      <Label htmlFor="mentions-title">Titre de la page</Label>
                      <Input
                        id="mentions-title"
                        value={mentionsContent.title}
                        onChange={(e) => setMentionsContent({ ...mentionsContent, title: e.target.value })}
                        placeholder="Mentions Légales"
                        className="mt-1.5"
                        data-testid="mentions-title-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mentions-content">Contenu (Markdown supporté)</Label>
                      <Textarea
                        id="mentions-content"
                        value={mentionsContent.content}
                        onChange={(e) => setMentionsContent({ ...mentionsContent, content: e.target.value })}
                        placeholder="## Éditeur du site..."
                        rows={15}
                        className="mt-1.5 font-mono text-sm"
                        data-testid="mentions-content-input"
                      />
                    </div>
                    <div className="flex justify-between items-center pt-4">
                      <a 
                        href="/mentions-legales" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-tp-primary hover:underline"
                      >
                        Voir la page →
                      </a>
                      <Button 
                        onClick={() => saveLegalPage('mentions_legales')}
                        disabled={savingMentions}
                        data-testid="save-mentions-button"
                      >
                        {savingMentions ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Sauvegarde...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Sauvegarder
                          </>
                        )}
                      </Button>
                    </div>
                  </TabsContent>
                </Tabs>
              )}
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <WeeklyReportSettings />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminSettingsPage;
